<template>
  <v-col class="pa-6">
    <!-- Back Button -->
    <v-btn color="#4390c0" @click="$router.push('/subscriptions')" class="mb-4 white--text">
      Back
    </v-btn>

    <!-- Loading -->
    <v-row justify="center" v-if="loading">
      <v-progress-circular indeterminate color="#4390c0" size="64"></v-progress-circular>
    </v-row>

    <!-- Doctor Card (only show if not upgrading and doctor is loaded) -->
    <v-row v-else-if="!selectedDoctor && doctor" justify="center">
      <v-col cols="12" md="6">
        <v-card class="doctor-card mx-auto pa-6" elevation="4">
          <v-row no-gutters align="center">
            <v-col cols="12" class="text-center mb-4">
              <v-avatar v-if="!doctor.profilePhoto" size="100" class="doctor-avatar mb-3">
                <v-icon size="60" color="#4390c0">mdi-account</v-icon>
              </v-avatar>
              <img style="height: 100px" v-if="doctor.profilePhoto" :src="doctor.profilePhoto.url">
              <h2 v-if="doctor">{{ doctor.fullName }}</h2>

              <v-chip :color="subscription ? '#4390c0' : 'grey'" text-color="white" small class="mt-1">
                {{ subscription?.packageName || "Free Plan" }}
              </v-chip>
            </v-col>

            <v-divider class="my-3"></v-divider>

            <v-col cols="12" v-if="doctor" class="d-flex mb-4 flex-column align-center justify-center">
              <div class="info-line d-flex align-center mb-2">
                <v-icon color="#4390c0" class="mr-2">mdi-email</v-icon>
                <span>{{ doctor.email }}</span>
              </div>
              <div class="info-line d-flex align-center mb-2">
                <v-icon color="#4390c0" class="mr-2">mdi-stethoscope</v-icon>
                <span>{{ doctor.profession[0] }}</span>
              </div>
              <div class="info-line d-flex align-center mb-2">
                <v-icon color="#4390c0" class="mr-2">mdi-phone</v-icon>
                <span>{{ doctor.CellNo }}</span>
              </div>
              <div class="info-line d-flex align-center mb-2">
                <v-icon color="#4390c0" class="mr-2">mdi-calendar-clock</v-icon>
                <span><strong>Consults Left:</strong> {{ subscription?.consultsLeft ?? 0 }}</span>
              </div>
              <div class="info-line d-flex align-center mb-2">
                <v-icon color="#4390c0" class="mr-2">mdi-calendar</v-icon>
                <span><strong>Expiry:</strong> {{ formatDate(subscription?.consultsExpiry) }}</span>
              </div>
            </v-col>

            <v-col cols="12" class="text-center">
              <v-btn color="#e022d1" class="white--text px-6 py-3 text-none" rounded @click="openUpgradeDialog(doctor)">
                <v-icon left>mdi-arrow-up-bold-circle-outline</v-icon>
                Upgrade Subscription
              </v-btn>
            </v-col>
            <v-col cols="12" class="text-center mt-4" v-if="subscription && subscription.packageName !== 'Free'">
              <v-btn color="#4390c0" class="white--text px-6 py-3 text-none" rounded @click="cancellingSubscription = true">
                <v-icon left>mdi-cancel</v-icon>
                Cancel Subscription
              </v-btn>
            </v-col>

          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- If doctor isn't loaded and not loading, show a friendly message -->
    <v-row v-else-if="!loading && !doctor && !selectedDoctor" justify="center">
      <v-col cols="12" md="6" class="text-center">
        <v-card class="pa-6">
          <p>No doctor data found. Please check the URL or try again.</p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upgrade Packages (only show if upgrading) -->
    <v-col v-if="selectedDoctor" class="upgrade-section">
      <h3 class="mt-4 text-center">Upgrade to Premium</h3>
      <v-row justify="center" class="ma-0">
        <v-col class="pa-0" cols="6" md="3" v-for="option in options" :key="option.name">
          <v-card class="sub-card my-4 pa-4 mx-3">
            <v-row justify="center" class="ma-0">
              <h2>{{ option.name }}</h2>
            </v-row>
            <v-row justify="center" class="ma-0">
              <h3 class="mt-3 text-center">{{ option.jobs }}</h3>
            </v-row>
            <v-row justify="center" class="ma-0">
              <h3 class="text-center">Consults(s) p/m</h3>
            </v-row>
            <v-row justify="center" class="ma-0">
              <h1>R{{ option.price }}</h1>
            </v-row>
            <v-row justify="center" class="ma-0">
              <v-btn @click="viewPackage(option)" dark rounded class="pink-btn mt-2">
                View
              </v-btn>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-col>

    <!-- Selected Package Checkout -->
    <v-col v-if="viewingPackage && selectedPackage" class="pa-0 mt-6">
      <v-row justify="center">
        <v-card width="80%" class="pa-4" style="border-radius: 16px;">
          <v-row class="ma-0 d-flex align-center justify-space-between">
            <h3 class="font-weight-semibold">{{ selectedPackage.name }} Package</h3>
            <p class="text-h6 mb-0 font-weight-bold" style="color:#222">
              R{{ selectedPackage.price }}
            </p>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <v-row class="ma-0">
            <p class="text-body-1 mb-0">{{ selectedPackage.jobs }} Consultations p/m</p>
          </v-row>

          <v-row v-if="subscription" class="ma-0 mt-2">
            <p v-if="subscription && subscription.packageName == selectedPackage.name"
              class="text-body-2 font-weight-medium" style="color:#004aad;">
              Current Package Active
            </p>
            <p v-if="!canUpgrade && subscription && subscription.packageName != selectedPackage.name"
              class="text-body-2 font-weight-medium" style="color:#004aad;">
              Higher Package Available
            </p>
          </v-row>
        </v-card>
      </v-row>

      <!-- Checkout & Back Buttons -->
      <v-row justify="center" class="mt-4">
        <v-col cols="12" md="auto" class="d-flex justify-center">
          <v-btn v-if="canUpgrade" @click="upgradePackage" :disabled="loading" dark rounded class="pink-btn mb-3"
            width="220px">
            Upgrade
          </v-btn>

        </v-col>

        <v-col cols="12" md="auto" class="d-flex justify-center">
          <v-btn @click="viewingPackage = false" dark rounded class="blue-btn" width="220px">
            Back
          </v-btn>
        </v-col>
      </v-row>
    </v-col>

    <v-dialog v-model="promptDialog" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="red" v-if="promptType == 'error'">
            mdi-alert-circle
          </v-icon>
          <v-icon size="96" color="green" v-if="promptType == 'success'">
            mdi-check-circle
          </v-icon>
          <div class="text-h5 my-6" v-if="promptType == 'error'">Error</div>
          <div class="text-h5 my-6" v-if="promptType == 'success'">Success</div>
          <div class="body-1 text-h6">{{ promptMessage }}</div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="black" dark depressed large style="border-radius: 24px" @click="promptDialog = false">
            CLOSE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="480px" v-model="cancellingSubscription">
      <v-card width="100%" class="pa-8">
        <v-row justify="center" class="ma-0 text-center">
          <h3>Are you sure you want to cancel the subscription?</h3>
        </v-row>
        <v-row justify="center" class="ma-0">
          <v-btn class="mt-4" @click="cancellingSubscription = false" dark rounded color="black" width="240px">
            BACK
          </v-btn>
          <v-btn class="mt-4" @click="cancelSubscription()" dark rounded color="#e022d1" width="240px">
            CONFIRM
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { getDoc, doc, updateDoc, Timestamp, getFirestore } from "firebase/firestore";
import { firebaseApp } from "../../firebaseConfig";
import { sendPushNotificationUser } from "@/utils/firestoreUtil";

const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      doctor: null,
      selectedDoctor: null,
      subscription: null,
      options: [
        { jobs: "10", price: 499, name: "Bronze", freeAdsLeft: 0, adsDiscount: 0, articlesDiscount: 0 },
        { jobs: "15", price: 699, name: "Silver", freeAdsLeft: 0, adsDiscount: 5, articlesDiscount: 5 },
        { jobs: "20", price: 999, name: "Gold", freeAdsLeft: 1, adsDiscount: 15, articlesDiscount: 15 },
        { jobs: "Unlimited", price: 1499, name: "Platinum", freeAdsLeft: 2, adsDiscount: 20, articlesDiscount: 20 },
      ],
      selectedPackage: null,
      viewingPackage: false,
      loading: false,
      promptDialog: false,
      promptMessage: "",
      promptType: "",
      cancellingSubscription: false,
    };
  },

  mounted() {
    this.fetchDoctor();
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return "N/A";
      if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleDateString();
      if (typeof timestamp === "string") return timestamp;
      if (timestamp instanceof Date) return timestamp.toLocaleDateString();
      return "N/A";
    },

    async fetchDoctor() {
      try {
        this.loading = true;
        const doctorId = this.$route.query.doctorId;
        if (!doctorId) {
          console.error("No doctorId found in URL");
          return;
        }

        const doctorRef = doc(db, "doctors", doctorId);
        const doctorSnap = await getDoc(doctorRef);

        if (doctorSnap.exists()) {
          const data = doctorSnap.data();
          data.id = doctorSnap.id;
          this.doctor = data;
          this.subscription = data.subscription;
        } else {
          this.doctor = null;
        }
      } catch (err) {
        console.error("Error fetching doctor:", err);
        this.promptType = "error";
        this.promptMessage = "Failed to load doctor data.";
        this.promptDialog = true;
      } finally {
        this.loading = false;
      }
    },

    openUpgradeDialog(doctor) {
      this.selectedDoctor = doctor;
      this.viewingPackage = false;
    },

    viewPackage(option) {
      this.selectedPackage = option;
      this.viewingPackage = true;
    },

    async upgradePackage() {
      if (!this.selectedDoctor || !this.selectedPackage) return;

      try {
        this.loading = true;

        const now = new Date();
        const expiry = new Date();
        expiry.setMonth(expiry.getMonth() + 1);

        const newSubscription = {
          activationDate: Timestamp.fromDate(now),
          consultsExpiry: Timestamp.fromDate(expiry),
          consultsLeft: parseInt(this.selectedPackage.jobs) || 0,
          packageName: this.selectedPackage.name,
          freeAdsLeft: this.selectedPackage.freeAdsLeft,
          adsDiscount: this.selectedPackage.adsDiscount,
          articlesDiscount: this.selectedPackage.articlesDiscount,
        };

        await updateDoc(doc(db, "doctors", this.selectedDoctor.id), {
          subscription: newSubscription,
        });

        this.subscription = newSubscription;
        this.loading = false;
        this.promptMessage = `Successfully upgraded to ${this.selectedPackage.name} package!`;
        this.promptType = "success";
        this.promptDialog = true;

        if (this.selectedDoctor.fcmToken) {
          await sendPushNotificationUser(
            "Subscription Upgraded",
            `You have successfully upgraded to the ${this.selectedPackage.name} package!`,
            this.selectedDoctor.fcmToken
          );
        }

        setTimeout(() => {
          this.$router.go();
        }, 3000);
      } catch (err) {
        console.error(err);
        this.loading = false;
        this.promptType = "error";
        this.promptMessage = "Failed to upgrade subscription.";
        this.promptDialog = true;
      }
    },

    async cancelSubscription() {
      if (!this.doctor) return;

      try {
        this.loading = true;
        const newSubscription = {
          activationDate: Timestamp.fromDate(new Date()),
          consultsExpiry: Timestamp.fromDate(new Date()),
          consultsLeft: 0,
          packageName: "Free",
          freeAdsLeft: 0,
          adsDiscount: 0,
          articlesDiscount: 0,
        };

        await updateDoc(doc(db, "doctors", this.doctor.id), {
          subscription: newSubscription,
        });

        this.subscription = newSubscription;
        this.loading = false;

        this.promptMessage = "Subscription cancelled successfully!";
        this.promptType = "success";
        this.promptDialog = true;

        if (this.doctor.fcmToken) {
          await sendPushNotificationUser(
            "Subscription Cancelled",
            "Your subscription has been cancelled successfully.",
            this.doctor.fcmToken
          );
        }

        setTimeout(() => this.$router.go(), 3000);
      } catch (err) {
        console.error(err);
        this.loading = false;
        this.promptMessage = "Error cancelling subscription.";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
  },

  computed: {
    canUpgrade() {
      const current = this.subscription?.packageName;
      const target = this.selectedPackage?.name;
      if (!current || !target) return false;
      const levels = ["Free", "Bronze", "Silver", "Gold", "Platinum"];
      return levels.indexOf(target) > levels.indexOf(current);
    },
  },
};
</script>


<style scoped>
.doctor-card {
  max-width: 480px;
  background: linear-gradient(145deg, #ffffff, #f2f7fb);
  border-radius: 18px;
  box-shadow: 0 4px 14px rgba(0, 40, 86, 0.1);
  transition: all 0.25s ease;
}

.doctor-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 40, 86, 0.15);
}

.doctor-avatar {
  background-color: #e6f0fa !important;
  border: 2px solid #4390c0;
}

.doctor-name {
  color: #002856;
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 4px;
}

.info-line {
  display: flex;
  align-items: center;
  margin: 6px 0;
  font-size: 14px;
  color: #444;
}

.v-divider {
  border-color: rgba(0, 40, 86, 0.1) !important;
}

.sub-card {
  border-radius: 20px !important;
  border: 1px solid black;
  box-shadow: 4px 4px 8px grey !important;
  background-color: lightgrey;
}
</style>
